import { useQuery } from "@tanstack/react-query";
import { getTutorInterventionsTutorId, getTutorSocrateRating } from "@/api/tutors.ts";
import { useEffect, useRef, useState } from "react";
import { useAssignedChatStore } from "@/stores/useAssignedChatStore.ts";
import { useUserStore } from "@/stores/useUserStore.ts";
import { log } from "@/helpers/console.ts";
import { PollingStates, useChatStore } from "@/stores/useChatStore.ts";
import { interventionStorageCollection } from "@/collections/interventionStorageCollection.ts";
import { useSignalStore } from "@/stores/useSignalrStore.ts";

// This function polls an endpoint to receive a list of interventions assigned to our tutor
// It's done as a fallback in case signalr connection fails
export const useHookOldSessionFetch = () => {
  const hasLoadedPrevSessions = useRef<boolean>(false);
  const { AddNewSession, SetSessionCompleted, UpdateSessionDetails } = useAssignedChatStore();
  const { userId, tutorId } = useUserStore();
  const { AddNewChat } = useChatStore();
  const { signalr } = useSignalStore();

  const [fetchOnce, setFetchOnce] = useState(false);

  const myInterventions = useQuery({
    queryFn: () => getTutorInterventionsTutorId(tutorId, !hasLoadedPrevSessions.current),
    queryKey: ["tutor-intervention", tutorId],
    refetchInterval: 1000 * 15,
    enabled: !fetchOnce,
  });

  useEffect(() => {
    if (!hasLoadedPrevSessions.current && myInterventions.isFetched) {
      hasLoadedPrevSessions.current = true;
      setFetchOnce(true);
    }

    const activeInterventions = myInterventions.data?.filter(
      (intervention) => intervention.dateEnded === null,
    );

    const oldInterventions = myInterventions.data?.filter(
      (intervention) => intervention.dateEnded !== null,
    );

    if (activeInterventions && signalr) {
      const addedFlowSessionId: Array<number> = [];
      for (const intervention of activeInterventions) {
        // Have we already processed this session?
        if (addedFlowSessionId.find((item) => item === intervention.flowGeneratorSessionId))
          continue;

        const { assignedSessions: activeSessions } = useAssignedChatStore.getState();

        // Is this session already assigned to us?
        const currentSession = activeSessions.get(intervention.flowGeneratorSessionId);

        if (currentSession) {
          if (!currentSession.assignedChat.flowGeneratorSessionInterventionId) {
            const { userId } = useUserStore.getState();
            UpdateSessionDetails(currentSession.assignedChat.flowGeneratorSessionId, {
              flowGeneratorSessionInterventionId: intervention.flowGeneratorSessionInterventionId,
              dateTutorJoined: intervention.dateTutorJoined ?? new Date(0),
              flowGeneratorSessionId: intervention.flowGeneratorSessionId,
              tutorId: intervention.tutorId,
              tutorUserId: userId,
            });
          }

          if (!currentSession.completed) continue;
        }

        log("Adding stale/missing session", intervention);

        addedFlowSessionId.push(intervention.flowGeneratorSessionId);

        AddNewSession({
          assignedChat: {
            flowGeneratorSessionId: intervention.flowGeneratorSessionId,
            flowGeneratorSessionInterventionId: intervention.flowGeneratorSessionInterventionId,
            dateTutorJoined: intervention.dateTutorJoined ?? new Date(0),
            tutorId: intervention.tutorId,
            tutorUserId: userId,
          },
        });

        const { chats } = useChatStore.getState();
        if (!chats.get(intervention.flowGeneratorSessionId)) {
          AddNewChat({
            flowGeneratorSessionId: intervention.flowGeneratorSessionId,
          });
        }
      }
    }

    if (oldInterventions && signalr) {
      const addedFlowSessionId: Array<number> = [];
      for (const intervention of oldInterventions) {
        // Have we already processed this session?
        if (addedFlowSessionId.find((item) => item === intervention.flowGeneratorSessionId))
          continue;

        const { assignedSessions: activeSessions } = useAssignedChatStore.getState();

        // Is this session already assigned to us?
        const currentSession = activeSessions.get(intervention.flowGeneratorSessionId);

        if (currentSession) {
          // In some cases (e.g. we closed the laptop) causes some sessions to stay active when they're actually finished
          if (
            !currentSession.completed &&
            intervention.flowGeneratorSessionInterventionId ===
              currentSession.assignedChat.flowGeneratorSessionInterventionId
          ) {
            SetSessionCompleted(intervention.flowGeneratorSessionId);
          }

          continue;
        }

        log("Adding old intervention", intervention);

        const newSession = (feedbackGiven: boolean, socratesId: string | null) => {
          AddNewSession({
            assignedChat: {
              flowGeneratorSessionId: intervention.flowGeneratorSessionId,
              flowGeneratorSessionInterventionId: intervention.flowGeneratorSessionInterventionId,
              dateTutorJoined: intervention.dateTutorJoined ?? new Date(0),
              tutorId: intervention.tutorId,
              tutorUserId: userId,
            },
            completed: true,
            feedbackGiven,
          });

          const { chats } = useChatStore.getState();

          // Sometimes we may have already got this chat open (e.g., shared session)
          if (!chats.get(intervention.flowGeneratorSessionId)) {
            AddNewChat({
              flowGeneratorSessionId: intervention.flowGeneratorSessionId,
              socrates: {
                session:
                  socratesId && socratesId !== ""
                    ? {
                        status: "Previous Session",
                        learnerSessionId: socratesId,
                      }
                    : undefined,
                state:
                  socratesId && socratesId !== "" ? PollingStates.Ready() : PollingStates.Idle(),
              },
              isStartupFinished: true, // Don't listen in for now
            });
          }
        };

        const existingData = interventionStorageCollection.get(intervention.flowGeneratorSessionId);
        if (existingData) {
          newSession(existingData.isFeedbackComplete, existingData.lastSocratesSessionId);
        } else {
          getTutorSocrateRating(
            intervention.tutorId,
            intervention.flowGeneratorSessionInterventionId,
          ).then((response) => {
            const feedbackGiven =
              response.tutorSatisfactionRating !== null && response.tutorSatisfactionRating >= 0;

            newSession(feedbackGiven, null);
          });
        }

        addedFlowSessionId.push(intervention.flowGeneratorSessionId);
      }
    }
  }, [
    AddNewChat,
    AddNewSession,
    SetSessionCompleted,
    UpdateSessionDetails,
    myInterventions.data,
    myInterventions.isFetched,
    signalr,
    userId,
  ]);
};
